import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Table,
  Pagination,
  Row,
  Col,
  Tooltip,
  Tag,
  Drawer,
  message,
  Collapse,
  Button,
  Modal,
} from "antd";
import axios from "axios";
import moment from "moment";

import { formatDateTime } from "../../helpers/date.js";
import TransactionButtons from "./TransactionButtons.js";
import {
  InfoCircleOutlined,
  ReloadOutlined,
  CheckOutlined,
  MinusOutlined,
  CaretRightOutlined,
  FileSyncOutlined,
} from "@ant-design/icons";
import {
  INVOICE_STATUSES,
  INVOICE_STATUS_FAILED,
  INVOICE_STATUS_SUCCESS,
} from "../../constants/invoiceStatus.js";
import TransactionFilters from "./TransactionFilters.js";

const now = moment();

const TopupList = () => {
  const [transactions, setTransactions] = useState();
  const [filters, setFilters] = useState({});
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 50 });
  const [isLoading, setIsLoading] = useState(false);

  const formattedFilters = useMemo(
    () => ({
      types: "Top Up",
      ...(filters.invoiceStatus && {
        invoiceStatus: filters.invoiceStatus,
      }),
      ...(filters.invoiceIsConsolidate && {
        invoiceIsConsolidate: filters.invoiceIsConsolidate,
      }),
      limit: pagination.limit,
      page: pagination.currentPage,
      ...(filters.search && {
        searchField: filters.searchField,
        search: filters.search,
      }),
      ...(filters.startDate !== null && {
        startDate: filters.startDate?.startOf("day")?.toISOString(),
      }),
      ...(filters.endDate !== null && {
        endDate: filters.endDate?.endOf("day")?.toISOString(),
      }),
      ...(filters.tagging && { tagging: filters.tagging }),
    }),
    [pagination.currentPage, pagination.limit, filters]
  );

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    const res = await axios.get(`api/transactions`, {
      params: formattedFilters,
    });
    setTransactions(res.data.data);
    setPagination(res.data.pagination);
    setIsLoading(false);
  }, [formattedFilters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleRecheckInvoice = (paymentId = null) => {
    const isCheckAll = paymentId === null;

    axios
      .post(`api/payment/check-invoice`, {
        paymentId: !isCheckAll
          ? [paymentId]
          : transactions.map((transaction) => transaction.objectId),
      })
      .then(() => {
        fetchTransactions();

        if (isCheckAll) {
          Modal.info({
            title: "Invoice Re-check initialized",
            content: (
              <div>
                <p>
                  All non-success Invoices in this page have started re-checking
                </p>
                <p>Please check again after 1 minute</p>
              </div>
            ),
            onOk() {},
          });
        }
      })
      .catch((error) =>
        message.error(error.response?.data?.message || error.message, 0)
      );
  };

  const handleRecreateInvoice = (paymentId) => {
    axios
      .post(`api/payment/create-invoice`, { paymentId })
      .then(() => {
        fetchTransactions();

        Modal.info({
          title: "Invoice Re-create initialized",
          content: (
            <div>
              <p>Please check again after few minutes</p>
            </div>
          ),
          onOk() {},
        });
      })
      .catch((error) =>
        message.error(error.response?.data?.message || error.message, 0)
      );
  };

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "objectId",
      width: 140,
    },
    {
      title: "User Email",
      dataIndex: "merchant",
      key: "username",
      width: 200,
      render: (text, transaction) => (
        <>
          <Tooltip title={text?.user?.username || "-"}>
            {text?.user?.email || "-"}{" "}
          </Tooltip>
          { transaction.user && <Tooltip title={`Topup performed by SubUser`}>
            <InfoCircleOutlined />
          </Tooltip>}
        </>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      width: 100,
      render: (text) => (text ? text.toFixed(2) : "-"),
    },
    {
      title: "Method",
      dataIndex: "method",
      width: 100,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
    {
      title: "Topup At",
      dataIndex: "creditSuccessAt",
      render: (text) => (text ? formatDateTime(text) : "Failed"),
    },
    {
      title: "User Tagging",
      dataIndex: "merchant",
      key: "tagging",
      width: 120,
      render: (text) => text?.user?.tagging || "-",
    },
    {
      title: "Consolidate",
      dataIndex: "invoice",
      width: 100,
      render: (invoice) => {
        if (!invoice) {
          return null;
        }

        return invoice?.isConsolidate ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <MinusOutlined style={{ color: "red" }} />
        );
      },
    },
    {
      title: "Invoice",
      dataIndex: "invoice",
      key: "invoice",
      width: 220,
      render: (invoice, payment) => {
        if (!invoice?.status) {
          return "-";
        }

        const status = INVOICE_STATUSES[invoice.status];
        if (!status) {
          return <Tag>{invoice.status}</Tag>;
        }

        return (
          <>
            <Tooltip
              placement="topLeft"
              title={INVOICE_STATUSES[invoice.status]?.desc}
            >
              <Tag color={INVOICE_STATUSES[invoice.status].color}>
                {invoice.status}
              </Tag>
            </Tooltip>
            <Tooltip placement="topLeft" title="More info" className="mr-2">
              <InfoCircleOutlined onClick={() => setSelectedInvoice(invoice)} />
            </Tooltip>
            {![INVOICE_STATUS_FAILED, INVOICE_STATUS_SUCCESS].includes(
              invoice.status
            ) &&
              now.diff(moment(payment.creditSuccessAt?.iso), "minutes") >=
                5 && (
                <Tooltip placement="topLeft" title="Re-check Invoice Status">
                  <ReloadOutlined
                    onClick={() => handleRecheckInvoice(payment.objectId)}
                  />
                </Tooltip>
              )}
            {[INVOICE_STATUS_FAILED].includes(
              invoice.status
            ) &&
              now.diff(moment(payment.creditSuccessAt?.iso), "minutes") >=
                5 && (
                <Tooltip placement="topLeft" title="Re-Create Invoice">
                  <FileSyncOutlined 
                    onClick={() => handleRecreateInvoice(payment.objectId)}
                  />
                </Tooltip>
              )}
          </>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <TransactionFilters
            filters={filters}
            onFiltersChange={(filters) => setFilters(filters)}
          />
        </Collapse.Panel>
      </Collapse>

      <Row className="mt-2 mb-2" justify="space-between">
        <div className="flex">
          <TransactionButtons
            filters={{
              types: "Top Up",
              ...(filters.invoiceStatus && {
                invoiceStatus: filters.invoiceStatus,
              }),
              ...(filters.invoiceIsConsolidate && {
                invoiceIsConsolidate: filters.invoiceIsConsolidate,
              }),
              ...(filters.startDate !== null && {
                startDate: filters.startDate?.startOf("day")?.toISOString(),
              }),
              ...(filters.endDate !== null && {
                endDate: filters.endDate?.endOf("day")?.toISOString(),
              }),
              ...(filters.search && {
                searchField: filters.searchField,
                search: filters.search,
              }),
              ...(filters.tagging && { tagging: filters.tagging }),
              pagination: pagination,
            }}
          />

          <div>
            <Button onClick={() => handleRecheckInvoice(null)}>
              Recheck All Invoice <ReloadOutlined></ReloadOutlined>
            </Button>
          </div>
        </div>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={transactions}
        pagination={false}
        scroll={{ x: 1300, y: 600 }}
        loading={isLoading}
      />

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      {selectedInvoice && (
        <Drawer
          width={736}
          title="Top Up Invoice"
          closable={{ "aria-label": "Close Button" }}
          onClose={() => setSelectedInvoice(null)}
          visible={selectedInvoice}
        >
          {Object.entries(selectedInvoice).map(([key, value]) => {
            return (
              <p key={key}>
                <strong>{key}</strong>: {JSON.stringify(value)}
              </p>
            );
          })}
        </Drawer>
      )}
    </React.Fragment>
  );
};

export default TopupList;
